import { useMemo, useState } from 'react';
import { Check, LoaderCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SOPGenerationActivity } from '@/types';
import { groupActivity, shortTime } from './thought-activity';

/**
 * The generator thinking out loud.
 *
 * A run reports many small lines — what it read, what the model gave back, what it pulled
 * out, how each check came out, what the judge said — and a flat list of them reads as
 * noise. Grouped by the phase that produced them and hung on one continuous rail, the same
 * stream reads as a chain of thought: finished phases carry a check, the live one pulses,
 * and the rail says "this is one run" at a glance.
 *
 * Shared by the live generation panel and the history panel, so a run reads the same while
 * it happens and a week later.
 */

interface ThoughtTimelineProps {
	activity: SOPGenerationActivity[];
	/** Pulse the newest group — true only while the run is still going. */
	live?: boolean;
	/** Show only the newest this many steps; the rest wait behind "Show earlier steps". */
	maxVisible?: number;
}

export function ThoughtTimeline({ activity, live = false, maxVisible }: ThoughtTimelineProps) {
	const thoughts = useMemo(() => groupActivity(activity), [activity]);
	const [showAll, setShowAll] = useState(false);
	if (thoughts.length === 0) return null;

	const hidden = showAll || maxVisible === undefined ? 0 : Math.max(0, thoughts.length - maxVisible);
	const visible = thoughts.slice(hidden);

	return (
		<>
		{hidden > 0 && (
			<button
				type='button'
				onClick={() => setShowAll(true)}
				className='mb-3 ml-[34px] rounded-md text-xs font-medium text-accent-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/40'
			>
				Show {hidden} earlier {hidden === 1 ? 'step' : 'steps'}
			</button>
		)}
		<ol>
			{visible.map((group, visibleIndex) => {
				const index = hidden + visibleIndex;
				const isCurrent = live && index === thoughts.length - 1;
				const isLast = index === thoughts.length - 1;
				return (
					<li key={`${group.at}-${index}`} className='flex gap-2.5'>
						{/* Rail: marker + connecting line, so the phases read as one run. */}
						<div className='flex flex-col items-center'>
							<span className='flex size-6 shrink-0 items-center justify-center rounded-full border border-gray-500 bg-white'>
								{isCurrent ? (
									<LoaderCircle className='size-3.5 animate-spin text-accent-purple' />
								) : (
									<Check className='size-3.5 text-gray-900' />
								)}
							</span>
							{!isLast && <span className='w-px flex-1 bg-gray-500' />}
						</div>

						<div className={cn('flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5', !isLast && 'pb-3')}>
							<div className='flex items-baseline justify-between gap-2'>
								<p className='min-w-0 text-[13px] leading-4.5 wrap-break-word text-text-body'>{group.step}</p>
								<span className='shrink-0 text-[11px] tabular-nums text-gray-700'>{shortTime(group.at)}</span>
							</div>
							<ul className='flex flex-col gap-0.5'>
								{group.lines.map((line, lineIndex) => (
									<li key={`${line.at}-${lineIndex}`} className='text-xs leading-4 wrap-break-word text-gray-800'>
										{line.message}
									</li>
								))}
							</ul>
						</div>
					</li>
				);
			})}
		</ol>
		{showAll && maxVisible !== undefined && thoughts.length > maxVisible && (
			<button
				type='button'
				onClick={() => setShowAll(false)}
				className='mt-2 ml-[34px] rounded-md text-xs font-medium text-accent-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/40'
			>
				Show fewer steps
			</button>
		)}
		</>
	);
}
